import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Upload, Users, Calendar, Library, Package, ClipboardCheck, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImportDialog from "../components/ImportDialog";

const TILES = [
  {
    key: "players",
    title: "Import Players",
    description: "Upload a CSV of player names, emails, phones, primary and secondary sections, and admin-only details (address, sizes, dietary, emergency contact).",
    icon: Users,
    sampleHeaders: ["name", "email", "phone", "address", "section", "secondary_section", "player_type", "jacket_size", "shirt_size", "medical_info", "dietary_info", "emergency_contact_name", "emergency_contact_phone", "emergency_contact_relationship"],
  },
  {
    key: "events",
    title: "Import Events",
    description: "Upload a CSV of rehearsals and concerts (title, date, type, location).",
    icon: Calendar,
    sampleHeaders: ["title", "event_type", "start_time", "end_time", "location", "description", "status"],
  },
  {
    key: "music",
    title: "Import Music Library",
    description: "Upload a CSV of pieces (number, title, composer, arranger, genre, solo, comments).",
    icon: Library,
    sampleHeaders: ["number", "title", "composer", "arranger", "genre", "solo", "comments"],
  },
  {
    key: "inventory",
    title: "Import Inventory",
    description: "Upload a CSV of instruments, uniforms, and accessories with all fields in one file.",
    icon: Package,
    sampleHeaders: ["category", "subcategory", "name", "asset_tag", "make", "finish", "serial_number", "size", "condition", "purchase_date", "purchase_price", "supplier", "notes"],
  },
  {
    key: "attendance",
    title: "Import Attendance",
    description: "Bulk-import historical attendance from your old spreadsheet. One row per (event, player, status) — perfect for migrating data when first joining Vivo.",
    icon: ClipboardCheck,
    sampleHeaders: ["event_date", "event_title", "player_email", "status", "notes"],
  },
];

const AdminImports = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-imports-page">
      <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="admin-imports-back">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Admin
      </Button>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Upload className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-4xl tracking-tight">Bulk Imports</h1>
          <p className="text-muted-foreground mt-1">Upload CSV files for any of the collections below.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <Card key={t.key} className="glass-card">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Icon className="w-5 h-5 text-primary" />
                  {t.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">{t.description}</p>
                <p className="text-xs text-muted-foreground font-mono break-all">
                  Expected headers: {t.sampleHeaders.join(", ")}
                </p>
                <Button
                  onClick={() => setOpen(t.key)}
                  className="w-full"
                  data-testid={`admin-imports-${t.key}-btn`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {TILES.map((t) => (
        <ImportDialog
          key={t.key}
          open={open === t.key}
          onOpenChange={(v) => setOpen(v ? t.key : null)}
          importType={t.key}
          onSuccess={() => setOpen(null)}
        />
      ))}
    </div>
  );
};

export default AdminImports;
